import { useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import { ArrowLeft } from "lucide-react"
import { Button } from "@/modules/shared/ui/components/button"
import { Spinner } from "@/modules/shared/ui/components/spinner"
import {
  fetchOffboardingByIdThunk,
  selectSelectedOffboarding,
  selectOffboardingDetailLoading,
  clearSelectedOffboarding,
} from "@/modules/offboarding/store/offboardingSlice"
import { selectCurrentOrganizationId } from "@/modules/organizations/store/organizationSlice"
import { selectWorkflows, fetchWorkflows } from "@/modules/onboarding/workflows/store/workflowSlice"
import type { AppDispatch, RootState } from "@/store"
import { OffboardingEmployeeCard } from "@/modules/offboarding/ui/components/OffboardingEmployeeCard"
import { OffboardingInfoCard } from "@/modules/offboarding/ui/components/OffboardingInfoCard"
import { OffboardingWorkflowsCard } from "@/modules/offboarding/ui/components/OffboardingWorkflowsCard"
import { OffboardingStatusCard } from "@/modules/offboarding/ui/components/OffboardingStatusCard"
import { OffboardingDocumentsCard } from "@/modules/offboarding/ui/components/OffboardingDocumentsCard"
import { OffboardingApprovalActions } from "@/modules/offboarding/ui/components/OffboardingApprovalActions"
import { isOffboardingTerminal } from "@/modules/offboarding/enums"

export default function OffboardingDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()
  const offboarding = useSelector(selectSelectedOffboarding)
  const isLoading = useSelector(selectOffboardingDetailLoading)
  const organizationId = useSelector((s: RootState) => selectCurrentOrganizationId(s))
  const workflows = useSelector(selectWorkflows)

  useEffect(() => {
    if (id) dispatch(fetchOffboardingByIdThunk(id))
    dispatch(fetchWorkflows())
    return () => { dispatch(clearSelectedOffboarding()) }
  }, [id, dispatch])

  const refresh = () => { if (id) dispatch(fetchOffboardingByIdThunk(id)) }

  if (isLoading && !offboarding) return <div className="flex items-center justify-center py-24"><Spinner className="size-6" /></div>
  if (!offboarding) return <div className="p-6 text-center text-muted-foreground">Offboarding record not found.</div>

  const isTerminal = isOffboardingTerminal(offboarding.resignationStatus)

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" onClick={() => navigate("/onboarding/offboarding")}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Offboarding Details</h1>
            <p className="text-sm text-muted-foreground mt-0.5">Record ID: {offboarding.id}</p>
          </div>
        </div>
        <OffboardingApprovalActions
          offboardingId={offboarding.id}
          organizationId={organizationId!}
          resignationStatus={offboarding.resignationStatus}
          onRefresh={refresh}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <OffboardingEmployeeCard
            firstName={offboarding.employeeFirstName}
            lastName={offboarding.employeeLastName}
            email={offboarding.employeeEmail}
            position={offboarding.employeePosition}
          />
          <OffboardingInfoCard
            offboardingId={offboarding.id}
            organizationId={organizationId!}
            offboardingReason={offboarding.offboardingReason}
            lastWorkingDate={offboarding.lastWorkingDate}
            initiatedBy={offboarding.initiatedBy}
            comments={offboarding.comments}
            isTerminal={isTerminal}
            onRefresh={refresh}
          />
          <OffboardingWorkflowsCard
            offboardingId={offboarding.id}
            organizationId={organizationId!}
            workflows={offboarding.workflows ?? []}
            availableWorkflows={workflows}
            isTerminal={isTerminal}
            onRefresh={refresh}
          />
        </div>

        <div className="space-y-6">
          <OffboardingStatusCard
            resignationStatus={offboarding.resignationStatus}
            terminationEligibleAt={offboarding.terminationEligibleAt}
            gracePeriodDays={offboarding.gracePeriodDays}
            createdAt={offboarding.createdAt}
            updatedAt={offboarding.updatedAt}
          />
          <OffboardingDocumentsCard
            offboardingId={offboarding.id}
            organizationId={organizationId!}
            documents={offboarding.documents}
            isTerminal={isTerminal}
            onRefresh={refresh}
          />
        </div>
      </div>
    </div>
  )
}
